'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Icon } from '@/lib/ui-kit/icons';
import { recentSearches, useRecentSearches } from '../core/recentStore';
import { useSearch } from '../core/SearchContext';
import type { RecentSearch } from '../core/types';
import { FOLD_EXIT } from './motion';
import { SearchLink } from './SearchLink';

const CHIP_BODY_CLASS =
  'flex h-full min-w-0 cursor-pointer items-center gap-1.5 pl-2 text-[12px] leading-none text-v2-text-primary outline-none';

/** A removed chip shrinks where it stands while its neighbours close the gap. */
const CHIP_EXIT = { opacity: 0, scale: 0.85, transition: { duration: 0.18 } };

/** The block fades in with the other sections, and folds up with its last chip gone. */
const SECTION_HIDDEN = { opacity: 0 };
const SECTION_SHOWN = { opacity: 1, transition: { duration: 0.2 } };

interface RecentChipProps {
  item: RecentSearch;
}

function RecentChip({ item }: RecentChipProps) {
  const t = useTranslations('search');
  const { select, setQuery } = useSearch();
  const handleRemove = () => recentSearches.remove(item.id);
  const handleQuery = () => setQuery(item.label);
  const handleOpen = () => select(item);

  const body = (
    <>
      <Icon name="Clock" size={14} className="shrink-0 text-v2-text-secondary" aria-hidden />
      <span className="max-w-[160px] truncate">{item.label}</span>
    </>
  );

  return (
    <motion.li
      layout
      exit={CHIP_EXIT}
      className="flex h-7 shrink-0 items-center gap-1 rounded-[10px] bg-v2-neutral-surface pr-1 dark:bg-v2-surface-tertiary"
    >
      {/* A query goes back into the field; an opened result opens again. */}
      {item.href ? (
        <SearchLink href={item.href} onClick={handleOpen} className={CHIP_BODY_CLASS}>
          {body}
        </SearchLink>
      ) : (
        <button type="button" onClick={handleQuery} className={CHIP_BODY_CLASS}>
          {body}
        </button>
      )}
      <button
        type="button"
        aria-label={t('removeRecent', { label: item.label })}
        onClick={handleRemove}
        className="flex size-5 shrink-0 cursor-pointer items-center justify-center text-v2-text-secondary transition-colors hover:text-v2-text-primary"
      >
        <Icon name="Close" size={10} aria-hidden />
      </button>
    </motion.li>
  );
}

/** "Recent searches" chips on one scrolling line, with Clear all — absent until there is one. */
export function RecentSearches() {
  const t = useTranslations('search');
  const items = useRecentSearches();
  const handleClear = () => recentSearches.clear();

  return (
    <AnimatePresence>
      {items.length > 0 ? (
        <motion.section
          key="recent"
          initial={SECTION_HIDDEN}
          animate={SECTION_SHOWN}
          exit={FOLD_EXIT}
          className="flex flex-col overflow-hidden"
        >
          <div className="flex h-[26px] items-center justify-between gap-3 pr-1 pl-2">
            <h3 className="text-[11.5px] font-medium leading-none text-v2-text-secondary">
              {t('recentSearches')}
            </h3>
            <button
              type="button"
              onClick={handleClear}
              className="flex h-[22px] cursor-pointer items-center rounded-[6px] px-1.5 text-[11.5px] leading-none text-v2-text-secondary transition-colors hover:bg-v2-neutral-surface hover:text-v2-text-primary dark:hover:bg-v2-surface-tertiary"
            >
              {t('clearAll')}
            </button>
          </div>
          <ul className="relative flex gap-1.5 overflow-x-auto px-2 pt-0.5 pb-1.5 [mask-image:linear-gradient(90deg,#000_calc(100%-24px),transparent)] [scrollbar-width:none]">
            <AnimatePresence mode="popLayout" initial={false}>
              {items.map((item) => (
                <RecentChip key={item.id} item={item} />
              ))}
            </AnimatePresence>
          </ul>
        </motion.section>
      ) : null}
    </AnimatePresence>
  );
}
